import React, { useState, useEffect } from "react";
import {
  FaChartLine,
  FaUsers,
  FaMousePointer,
  FaClock,
  FaEye,
  FaWpforms,
  FaTrashAlt,
  FaDownload,
  FaSync,
  FaArrowUp,
  FaArrowDown,
} from "react-icons/fa";
import { useAnalytics } from "../hooks/useAnalytics";
import { getAnalyticsData, clearAnalyticsData } from "../firebase";
import "./AnalyticsDashboard.css";

// Son N günün günlük ziyaretçi/dönüşüm sayılarını çıkarır (grafik için)
const buildDailyTrend = (sessions, days) => {
  const buckets = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDays = Math.min(days, 90);
  for (let i = maxDays - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    buckets.push({
      key: d.toISOString().split("T")[0],
      label: `${d.getDate()}.${d.getMonth() + 1}`,
      sessions: 0,
      conversions: 0,
    });
  }
  const index = new Map(buckets.map((b) => [b.key, b]));
  sessions.forEach((s) => {
    if (!s.startTime) return;
    const key = new Date(s.startTime).toISOString().split("T")[0];
    const b = index.get(key);
    if (b) {
      b.sessions++;
      if (s.outcome === "appointment_booked") b.conversions++;
    }
  });
  return buckets;
};

const AnalyticsDashboard = () => {
  const analytics = useAnalytics();
  const [rawSessions, setRawSessions] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState("overview");
  const [dateRange, setDateRange] = useState(30); // 7 / 30 / 90 / 0 (tümü)

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Tüm kullanıcıların oturumlarını sunucudan (Postgres) çek
      const completedSessions = await getAnalyticsData(1000);
      setRawSessions(completedSessions);
    } catch (error) {
      console.error("Error loading analytics data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Tarih aralığına göre filtrele + agregele (yeniden fetch gerekmeden)
  useEffect(() => {
    const cutoff = dateRange > 0 ? Date.now() - dateRange * 86400000 : 0;
    const sessions = rawSessions.filter(
      (s) => !cutoff || (s.startTime || 0) >= cutoff
    );
    setAnalyticsData({
      totalSessions: sessions.length,
      currentSession: null,
      completedSessions: sessions,
      aggregatedData: analytics.aggregateSessionData(sessions),
      dailyTrend: buildDailyTrend(sessions, dateRange > 0 ? dateRange : 30),
    });
  }, [rawSessions, dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (milliseconds) => {
    if (!milliseconds) return "0s";
    const seconds = Math.round(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value) => {
    return `${value}%`;
  };

  const clearAllAnalytics = async () => {
    if (
      window.confirm(
        "Tüm analytics verilerini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
      )
    ) {
      try {
        await clearAnalyticsData();
        analytics.clearAllData();
        await loadAnalyticsData();
      } catch (error) {
        console.error("Error clearing analytics data:", error);
      }
    }
  };

  const exportAnalytics = () => {
    if (!analyticsData) return;

    const exportData = {
      exportDate: new Date().toISOString(),
      summary: analyticsData.aggregatedData,
      totalSessions: analyticsData.totalSessions,
      completedSessions: analyticsData.completedSessions?.length || 0,
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

    const exportFileDefaultName = `analytics_export_${
      new Date().toISOString().split("T")[0]
    }.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-header">
          <h2>
            <FaChartLine /> Kullanıcı Analitikleri
          </h2>
          <p>Marketing ve kullanıcı davranış metrikleri</p>
        </div>
        <div className="analytics-loading">
          <div className="loading-spinner"></div>
          <p>Analytics verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  const aggregated = analyticsData?.aggregatedData;

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <div className="header-content">
          <h2>
            <FaChartLine /> Kullanıcı Analitikleri
          </h2>
          <p>Marketing ve kullanıcı davranış metrikleri</p>
        </div>
        <div className="header-actions">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="date-range-select"
            title="Tarih Aralığı"
          >
            <option value={7}>Son 7 gün</option>
            <option value={30}>Son 30 gün</option>
            <option value={90}>Son 90 gün</option>
            <option value={0}>Tüm zamanlar</option>
          </select>
          <button
            className="action-btn refresh-btn"
            onClick={loadAnalyticsData}
            title="Verileri Yenile"
          >
            <FaSync /> Yenile
          </button>
          <button
            className="action-btn export-btn"
            onClick={exportAnalytics}
            title="Verileri Dışa Aktar"
          >
            <FaDownload /> Dışa Aktar
          </button>
          <button
            className="action-btn danger-btn"
            onClick={clearAllAnalytics}
            title="Tüm Verileri Temizle"
          >
            <FaTrashAlt /> Temizle
          </button>
        </div>
      </div>

      {!analyticsData || analyticsData.totalSessions === 0 ? (
        <div className="no-data-container">
          <div className="no-data-content">
            <FaChartLine className="no-data-icon" />
            <h3>Henüz Analitik Verisi Yok</h3>
            <p>
              Kullanıcılar siteyi kullanmaya başladığında veriler burada
              görünecek.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="analytics-tabs">
            <button
              className={`tab-button ${
                selectedMetric === "overview" ? "active" : ""
              }`}
              onClick={() => setSelectedMetric("overview")}
            >
              <FaChartLine /> <span>Genel Bakış</span>
            </button>
            <button
              className={`tab-button ${
                selectedMetric === "engagement" ? "active" : ""
              }`}
              onClick={() => setSelectedMetric("engagement")}
            >
              <FaEye /> <span>Etkileşim</span>
            </button>
            <button
              className={`tab-button ${
                selectedMetric === "form" ? "active" : ""
              }`}
              onClick={() => setSelectedMetric("form")}
            >
              <FaWpforms /> <span>Form Analizi</span>
            </button>
            <button
              className={`tab-button ${
                selectedMetric === "devices" ? "active" : ""
              }`}
              onClick={() => setSelectedMetric("devices")}
            >
              <FaUsers /> <span>Cihaz Bilgileri</span>
            </button>
          </div>

          <div className="analytics-content">
            {selectedMetric === "overview" && (
              <div className="overview-section">
                <div className="stats-grid">
                  <div className="stat-card primary">
                    <div className="stat-icon">
                      <FaUsers />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {analyticsData.totalSessions}
                      </div>
                      <div className="stat-label">Toplam Oturum</div>
                    </div>
                  </div>

                  <div className="stat-card success">
                    <div className="stat-icon">
                      <FaChartLine />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {aggregated
                          ? formatPercentage(aggregated.conversionRate)
                          : "0%"}
                      </div>
                      <div className="stat-label">Dönüşüm Oranı</div>
                    </div>
                  </div>

                  <div className="stat-card info">
                    <div className="stat-icon">
                      <FaClock />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {aggregated
                          ? formatTime(aggregated.averageSessionDuration)
                          : "0s"}
                      </div>
                      <div className="stat-label">Ortalama Oturum</div>
                    </div>
                  </div>

                  <div className="stat-card warning">
                    <div className="stat-icon">
                      <FaWpforms />
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">
                        {aggregated
                          ? formatTime(aggregated.averageFormTime)
                          : "0s"}
                      </div>
                      <div className="stat-label">Ortalama Form Süresi</div>
                    </div>
                  </div>
                </div>

                {analyticsData?.dailyTrend &&
                  analyticsData.dailyTrend.length > 0 && (
                    <div className="trend-section">
                      <h3>Günlük Ziyaretçi Trendi</h3>
                      {(() => {
                        const trend = analyticsData.dailyTrend;
                        const maxVal = Math.max(
                          1,
                          ...trend.map((d) => d.sessions)
                        );
                        // 90 günde her günü etiketlemek kalabalık olur: aralıklı göster
                        const labelEvery = Math.ceil(trend.length / 15);
                        return (
                          <div className="trend-chart">
                            {trend.map((d, i) => (
                              <div
                                key={d.key}
                                className="trend-bar-wrap"
                                title={`${d.label}: ${d.sessions} oturum, ${d.conversions} randevu`}
                              >
                                <div className="trend-bar-track">
                                  <div
                                    className="trend-bar-fill"
                                    style={{
                                      height: `${(d.sessions / maxVal) * 100}%`,
                                    }}
                                  >
                                    {d.conversions > 0 && (
                                      <div
                                        className="trend-bar-conv"
                                        style={{
                                          height: `${
                                            (d.conversions / d.sessions) * 100
                                          }%`,
                                        }}
                                      ></div>
                                    )}
                                  </div>
                                </div>
                                <div className="trend-bar-label">
                                  {i % labelEvery === 0 ? d.label : ""}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                      <div className="trend-legend">
                        <span>
                          <i className="legend-dot sessions"></i> Oturum
                        </span>
                        <span>
                          <i className="legend-dot conversions"></i> Randevu
                        </span>
                      </div>
                    </div>
                  )}

                {aggregated?.stepProgression && (
                  <div className="funnel-section">
                    <h3>Dönüşüm Hunisi</h3>
                    <div className="funnel-container">
                      <div className="funnel-step">
                        <div className="analytics-step-content">
                          <div className="analytics-step-number">
                            {analyticsData.totalSessions}
                          </div>
                          <div className="analytics-step-title">Site Ziyareti</div>
                        </div>
                        <FaArrowDown className="step-arrow" />
                      </div>
                      <div className="funnel-step">
                        <div className="analytics-step-content">
                          <div className="analytics-step-number">
                            {aggregated.stepProgression.formToCalendar}
                          </div>
                          <div className="analytics-step-title">Form Tamamlandı</div>
                        </div>
                        <FaArrowDown className="step-arrow" />
                      </div>
                      <div className="funnel-step final">
                        <div className="analytics-step-content">
                          <div className="analytics-step-number success">
                            {aggregated.stepProgression.calendarToSuccess}
                          </div>
                          <div className="analytics-step-title">Randevu Alındı</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedMetric === "engagement" && (
              <div className="engagement-section">
                <h3>Komponent Etkileşim Analizi</h3>
                {aggregated?.mostEngagedComponents &&
                Object.keys(aggregated.mostEngagedComponents).length > 0 ? (
                  <div className="engagement-grid">
                    {Object.entries(aggregated.mostEngagedComponents)
                      .sort(([, a], [, b]) => b - a)
                      .map(([component, time]) => (
                        <div key={component} className="engagement-card">
                          <div className="engagement-header">
                            <FaEye className="engagement-icon" />
                            <h4>
                              {component
                                .replace("-", " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                            </h4>
                          </div>
                          <div className="engagement-value">
                            {formatTime(time)}
                          </div>
                          <div className="engagement-bar">
                            <div
                              className="engagement-fill"
                              style={{
                                width: `${
                                  (time /
                                    Math.max(
                                      ...Object.values(
                                        aggregated.mostEngagedComponents
                                      )
                                    )) *
                                  100
                                }%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="no-data-message">
                    <p>Henüz komponent etkileşim verisi bulunmuyor.</p>
                  </div>
                )}
              </div>
            )}

            {selectedMetric === "form" && (
              <div className="form-section">
                <h3>Form Analiz Detayları</h3>
                <div className="form-insights-grid">
                  <div className="insight-card">
                    <h4>Form Tamamlama Performansı</h4>
                    <div className="insight-stats">
                      <div className="insight-stat">
                        <span className="insight-label">
                          Ortalama Tamamlama Süresi:
                        </span>
                        <span className="insight-value">
                          {aggregated
                            ? formatTime(aggregated.averageFormTime)
                            : "0s"}
                        </span>
                      </div>
                      <div className="insight-stat">
                        <span className="insight-label">
                          Form Tamamlama Oranı:
                        </span>
                        <span className="insight-value success">
                          {analyticsData.totalSessions > 0
                            ? `${Math.round(
                                ((aggregated?.stepProgression?.formToCalendar ||
                                  0) /
                                  analyticsData.totalSessions) *
                                  100
                              )}%`
                            : "0%"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="insight-card">
                    <h4>Performans Metrikleri</h4>
                    <div className="insight-stats">
                      {aggregated?.timeMetrics && (
                        <>
                          <div className="insight-stat">
                            <span className="insight-label">
                              En Hızlı Tamamlama:
                            </span>
                            <span className="insight-value">
                              {formatTime(
                                aggregated.timeMetrics.fastestCompletion
                              )}
                            </span>
                          </div>
                          <div className="insight-stat">
                            <span className="insight-label">
                              En Yavaş Tamamlama:
                            </span>
                            <span className="insight-value">
                              {formatTime(
                                aggregated.timeMetrics.slowestCompletion
                              )}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedMetric === "devices" && (
              <div className="devices-section">
                <h3>Cihaz ve Tarayıcı Bilgileri</h3>
                <div className="device-grid">
                  {aggregated?.deviceInfo?.screenResolutions &&
                    Object.keys(aggregated.deviceInfo.screenResolutions)
                      .length > 0 && (
                      <div className="device-card">
                        <h4>Popüler Ekran Çözünürlükleri</h4>
                        <div className="device-list">
                          {Object.entries(
                            aggregated.deviceInfo.screenResolutions
                          )
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 5)
                            .map(([resolution, count]) => (
                              <div key={resolution} className="device-item">
                                <span className="device-name">
                                  {resolution}
                                </span>
                                <span className="device-count">
                                  {count} oturum
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {aggregated?.deviceInfo?.languages &&
                    Object.keys(aggregated.deviceInfo.languages).length > 0 && (
                      <div className="device-card">
                        <h4>Dil Tercihleri</h4>
                        <div className="device-list">
                          {Object.entries(aggregated.deviceInfo.languages)
                            .sort(([, a], [, b]) => b - a)
                            .slice(0, 5)
                            .map(([language, count]) => (
                              <div key={language} className="device-item">
                                <span className="device-name">{language}</span>
                                <span className="device-count">
                                  {count} oturum
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
